/**
 /**
 * Chunked Upload Service
 *
 * Handles the 3-step chunked upload flow:
 *   1. InitializeChunkedUpload  – register the session with the server
 *   2. UploadChunk              – send each 5 MB slice (0-based chunk numbering)
 *   3. FinalizeChunkedUpload    – tell the server to assemble the file
 *
 * Authorization is injected automatically by axiosClient's request interceptor.
 * Do NOT use these functions directly in components — use the useChunkedUpload hook instead.
 */

import axiosClient from "../api/axiosClient";
import { normalizeUploadFolderName } from "../utils/helpers";

/**
 * SHA-256 (hex) of a browser File's bytes, used for the exact-duplicate pre-check on
 * InitializeChunkedUpload. Reads the whole file into memory via file.arrayBuffer() --
 * the Web Crypto API has no incremental/streaming digest, so for very large files this
 * costs real memory. Acceptable here since this path only runs when the companion app
 * (which streams the hash from disk, see party-sharing-companion/server.js) isn't
 * available and the browser has to upload the file itself anyway.
 */
export const hashFileSha256 = async (file) => {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Step 1 — Initialize a chunked-upload session.
 *
 * @param {object} params
 * @param {number}       params.chatRoomId
 * @param {string}       params.fileName
 * @param {number}       params.totalSize        - exact file.size in bytes
 * @param {number}       params.totalChunks      - Math.ceil(totalSize / chunkSize)
 * @param {number}       params.chunkSize        - must be 5 * 1024 * 1024
 * @param {string}       params.contentType      - e.g. "video/mp4"
 * @param {string|null}  [params.contentHash]    - SHA-256 hex from hashFileSha256(), for the duplicate pre-check
 * @param {number|null}  [params.eventGalleryId]
 * @param {number|null}  [params.galleryAlbumId]
 * @param {boolean}      [params.isUploadedUsingData]
 * @param {boolean}      [params.isWatermark]
 * @param {boolean}      [params.preventSelfie]
 * @param {boolean}      [params.isCompressed]
 * @param {boolean}      [params.maxCompressed]
 * @param {boolean}      [params.forceCompress]
 *
 * @returns {Promise<{ SessionId: string, ChunkSize: number, TotalChunks: number }>}
 */
export const initializeChunkedUpload = ({
  chatRoomId,
  fileName,
  totalSize,
  totalChunks,
  chunkSize,
  contentType,
  contentHash = null,
  eventGalleryId = null,
  galleryAlbumId = null,
  isUploadedUsingData = false,
  isWatermark = false,
  isWatermarkDoneByCompanion = false,
  WMbyComanion = false,
  preventSelfie = false,
  isCompressed = false,
  maxCompressed = false,
  forceCompress = false,
}) => {
  const payload = {
    chatRoomId,
    fileName,
    totalSize,
    totalChunks,
    chunkSize,
    contentType,
    contentHash: contentHash || undefined,
    eventGalleryId,
    galleryAlbumId,
    isUploadedUsingData,
    isWatermark,
    isWatermarkDoneByCompanion,
    WMbyComanion: WMbyComanion || undefined,
    preventSelfie,
    isCompressed,
    maxCompressed,
    forceCompress,
  };

  console.log("[ChunkedUpload] Initialize payload", payload);
  return axiosClient.post("/Chat/Gallery/InitializeChunkedUpload", payload);
};

/**
 * Step 2 — Upload a single chunk.
 * Must be called sequentially for chunk 0, 1, 2, … (0-based).
 *
 * @param {object} params
 * @param {string}  params.sessionId   - SessionId from initializeChunkedUpload
 * @param {number}  params.chunkNumber - 0-based index
 * @param {Blob}    params.chunkData   - file.slice(start, end)
 * @param {string}  params.fileName    - original file name (used as blob filename)
 * @param {function} [onUploadProgress] - Axios progress callback (optional)
 *
 * @returns {Promise<{ ChunkNumber: number, Progress: number, IsComplete: boolean }>}
 */
export const uploadChunk = (
  { sessionId, chunkNumber, chunkData, fileName },
  onUploadProgress
) => {
  const formData = new FormData();
  formData.append("sessionId", sessionId);
  formData.append("chunkNumber", String(chunkNumber)); // ⚠️ 0-based — first chunk is 0
  formData.append("chunkData", chunkData, fileName);

  return axiosClient.post("/Chat/Gallery/UploadChunk", formData, {
    headers: { "Content-Type": undefined }, // let axios auto-set multipart boundary
    onUploadProgress,
  });
};

/**
 * Step 3 — Finalize the upload after all chunks have been sent successfully.
 * Only call this when every uploadChunk call returned without error.
 *
 * @param {object} params
 * @param {string}       params.sessionId
 * @param {string|null}  [params.folderName]   - e.g. "events"
 * @param {number|null}  [params.countryCode]  - e.g. 49
 *
 * @returns {Promise<any>}
 */
export const finalizeChunkedUpload = ({
  sessionId,
  folderName = null,
  countryCode = null,
  metaDateTime = null,
  WMbyComanion = false,
}) => {
  const payload = {
    sessionId,
    folderName: normalizeUploadFolderName(folderName),
    countryCode,
    metaDateTime,
    WMbyComanion: WMbyComanion || undefined,
  };

  console.log("[ChunkedUpload] Finalize payload", payload);
  return axiosClient.post("/Chat/Gallery/FinalizeChunkedUpload", payload);
};
